// src/components/services/PackageManager.ts
import api from './apiService';

export interface PackageIdentifierMapping {
  id: string;
  packageIdentifier: string;
  registryDisplayName: string;
  registryPublisher: string;
  productCode: string;
  upgradeCode: string;
  isVerified: boolean;
  isDefatomManaged: boolean;
  packageSource: string | number;
  priority: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string | null;
  keywords?: string[] | null;
  externalVendors?: ExternalVendor[] | null;
  schemaVersion?: string;
  _ts?: number;
}

export interface ExternalVendor {
  vendorName: string;
  softwareName: string;
}

export interface UpdateExternalVendorsPayload {
  packageIdentifier: string;
  externalVendors: ExternalVendor[] | null;
}

export interface PackageMappingListResponse {
  items: PackageIdentifierMapping[];
  count: number;
  continuationToken: string | null;
  hasMoreResults: boolean;
}

export interface PackageSearchResponse {
  packageIdentifiers: string[];
  count: number;
}

export interface ResponseModel<T> {
  isSuccess: boolean;
  message: string;
  data: T;
  errorCode?: string | null;
}

export type FilterType =
  | 'Unmapped'
  | 'ByNotVerified'
  | 'ByIsVerified'
  | 'Recent'
  | 'ByName'
  | 'ByPublisher'
  | 'ByProductCode'
  | 'ByUpgradeCode'
  | 'ByPackageIdentifier'
  | 'ByIsDefatomManaged';

export interface PackageMappingListParams {
  filterType?: FilterType;
  filterValue?: string;
  pageSize?: number;
  continuationToken?: string;
}

export interface UpdatePackageMappingPayload {
  packageIdentifier: string;
  isDefatomManaged: boolean;
  isVerified: boolean;
  notes: string;
}

export interface BulkPackageMappingItem {
  registryDisplayName: string;
  registryPublisher?: string;
  packageIdentifier: string;
  productCode?: string;
  upgradeCode?: string;
  isDefatomManaged?: boolean;
  priority?: number;
  isVerified?: boolean;
  packageSource?: string;
  notes?: string;
}

export interface BulkCreatePayload {
  mappings: BulkPackageMappingItem[];
}

export interface BulkUploadValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  validMappings: BulkPackageMappingItem[];
}

export interface WingetAppItem {
  packageIdentifier: string;
  packageName: string;
}

export interface WingetAppPayload {
  appsToAdd: WingetAppItem[];
}

export interface CustomAppPayload {
  packageIdentifier: string;
  packageVersion: string;
  packageName: string;
  architecture: string;
  installerUrl: string;
}

export interface PackageCVEItem {
  packageIdentifier: string;
  packageVersion: string;
}

export interface UpdatePackageCVEPayload {
  appsToUpdate: PackageCVEItem[];
}

export interface UpdateTenantCVEPayload {
  tenantId: string;
}

export interface BulkVerifyPayload {
  registryDisplayNames: string[];
}

export interface AgentInfo {
  agentId: string;
  agentInstalledDateTime: string;
  agentVersion: string;
  installedForUser: string;
  architecture: string;
}

export interface TenantAgents {
  tenantId: string;
  agents: AgentInfo[];
}

export interface SoftwareAgentsResponse {
  softwareName: string;
  packageIdentifier: string;
  tenants: TenantAgents[];
}

export const fetchPackageMappingList = async (
  params: PackageMappingListParams = {},
): Promise<ResponseModel<PackageMappingListResponse>> => {
  const queryParams = new URLSearchParams();

  if (params.filterType) {
    queryParams.append('filterType', params.filterType);
  }
  if (params.filterValue) {
    queryParams.append('filterValue', params.filterValue);
  }
  if (params.pageSize) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.continuationToken) {
    queryParams.append('continuationToken', params.continuationToken);
  }

  const response = await api.get<ResponseModel<PackageMappingListResponse>>(
    `/patchreposervice/PackageMapping/list?${queryParams.toString()}`,
  );

  return response.data;
};

export const searchPackageIdentifiers = async (query: string, maxResults: number = 10): Promise<ResponseModel<PackageSearchResponse>> => {
  const queryParams = new URLSearchParams();
  queryParams.append('query', query);
  queryParams.append('maxResults', maxResults.toString());

  const response = await api.get<ResponseModel<PackageSearchResponse>>(
    `/patchreposervice/PackageMapping/search-packages?${queryParams.toString()}`,
  );

  return response.data;
};

export const updatePackageMapping = async (id: string, payload: UpdatePackageMappingPayload): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>(`/patchreposervice/PackageMapping/${id}`, payload);

  return response.data;
};

export const fetchAgentsBySoftwareName = async (softwareName: string): Promise<ResponseModel<SoftwareAgentsResponse>> => {
  const queryParams = new URLSearchParams();
  queryParams.append('softwareName', softwareName);

  const response = await api.get<ResponseModel<SoftwareAgentsResponse>>(
    `/patchreposervice/AgentPackage/software/agents?${queryParams.toString()}`,
  );

  return response.data;
};

export const validateBulkUploadJSON = (data: any): BulkUploadValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const validMappings: BulkPackageMappingItem[] = [];

  // Check if data is an array or has a mappings property
  let mappingsArray: any[] = [];

  if (Array.isArray(data)) {
    mappingsArray = data;
  } else if (data && Array.isArray(data.mappings)) {
    mappingsArray = data.mappings;
  } else {
    errors.push('Invalid JSON structure. Expected an array or an object with "mappings" property.');
    return { isValid: false, errors, warnings, validMappings };
  }

  if (mappingsArray.length === 0) {
    errors.push('No mappings found in the uploaded file.');
    return { isValid: false, errors, warnings, validMappings };
  }

  // Validate each mapping
  mappingsArray.forEach((mapping, index) => {
    const itemErrors: string[] = [];

    // Required fields validation
    if (!mapping.packageIdentifier || typeof mapping.packageIdentifier !== 'string' || mapping.packageIdentifier.trim() === '') {
      itemErrors.push(`Item ${index + 1}: Missing or invalid "packageIdentifier" (required)`);
    }

    if (!mapping.registryDisplayName || typeof mapping.registryDisplayName !== 'string' || mapping.registryDisplayName.trim() === '') {
      itemErrors.push(`Item ${index + 1}: Missing or invalid "registryDisplayName" (required)`);
    }

    if (itemErrors.length > 0) {
      errors.push(...itemErrors);
    } else {
      // Validate and set defaults for optional fields
      const validMapping: BulkPackageMappingItem = {
        packageIdentifier: mapping.packageIdentifier.trim(),
        registryDisplayName: mapping.registryDisplayName.trim(),
        registryPublisher: mapping.registryPublisher?.trim() || '',
        productCode: mapping.productCode?.trim() || '',
        upgradeCode: mapping.upgradeCode?.trim() || '',
        isDefatomManaged: typeof mapping.isDefatomManaged === 'boolean' ? mapping.isDefatomManaged : true,
        priority: typeof mapping.priority === 'number' ? mapping.priority : 0,
        isVerified: typeof mapping.isVerified === 'boolean' ? mapping.isVerified : true,
        packageSource: mapping.packageSource?.trim() || 'None',
        notes: mapping.notes?.trim() || '',
      };

      validMappings.push(validMapping);

      // Optional warnings
      if (!mapping.registryPublisher || mapping.registryPublisher.trim() === '') {
        warnings.push(`Item ${index + 1}: "registryPublisher" is empty`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    validMappings,
  };
};

export const bulkCreatePackageMappings = async (payload: BulkCreatePayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/PackageMapping/bulk-create', payload);

  return response.data;
};

// Add this function after the bulkCreatePackageMappings function
export const bulkVerifyPackageMappings = async (payload: BulkVerifyPayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/PackageMapping/bulk-verify', payload);

  return response.data;
};

export const updateExternalVendors = async (payload: UpdateExternalVendorsPayload): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>('/patchreposervice/PackageMapping/update-external-vendors', payload);

  return response.data;
};

export const addAppFromWinget = async (payload: WingetAppPayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/wingetapi/add-apps', payload);

  return response.data;
};

export const addCustomApp = async (payload: CustomAppPayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/wingetapi/custom-app', payload);

  return response.data;
};

export const getPackageSourceLabel = (source: string | number): string => {
  if (typeof source === 'number') {
    const labels = ['None', 'Winget', 'Intune', 'Ninite', 'Chocolatey', 'Custom'];
    return labels[source] || 'Unknown';
  }
  return source || 'None';
};

export const getPackageSourceColor = (source: string | number): string => {
  const sourceStr = typeof source === 'number' ? getPackageSourceLabel(source) : source;

  switch (sourceStr?.toLowerCase()) {
    case 'winget':
      return 'text-blue-400';
    case 'intune':
      return 'text-purple-400';
    case 'ninite':
      return 'text-green-400';
    case 'chocolatey':
      return 'text-yellow-400';
    case 'custom':
      return 'text-orange-400';
    default:
      return 'text-gray-400';
  }
};

export const updatePackageCVE = async (payload: UpdatePackageCVEPayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/cveapp/update-cve', payload);

  return response.data;
};

export const updateAllTenantCVEs = async (payload: UpdateTenantCVEPayload): Promise<ResponseModel<any>> => {
  const response = await api.post<ResponseModel<any>>('/patchreposervice/cveapp/update-cve-by-tenant', payload);

  return response.data;
};
